import { Group, Text, UnstyledButton } from '@mantine/core';
import type { ReactNode } from 'react';
import classes from './StatusTabs.module.css';

export interface StatusTab<T extends string> {
  id: T;
  label: string;
  status: string;
  /** Green dot when true, red when false. */
  ok: boolean;
}

interface StatusTabsProps<T extends string> {
  tabs: StatusTab<T>[];
  value: T | null;
  onChange: (id: T) => void;
  label: string;
  /** Rendered after the tabs, e.g. an "Add" button. */
  after?: ReactNode;
}

/** Card-style tabs, each with a one-line status and a green/red dot. */
export function StatusTabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  after,
}: StatusTabsProps<T>) {
  return (
    <Group gap="sm" role="tablist" aria-label={label} align="stretch">
      {tabs.map(t => {
        const selected = t.id === value;
        return (
          <UnstyledButton
            key={t.id}
            role="tab"
            aria-selected={selected}
            className={classes.tab}
            data-selected={selected || undefined}
            onClick={() => onChange(t.id)}
          >
            <Text fw={600} size="sm">
              {t.label}
            </Text>
            <Group gap={7} wrap="nowrap">
              <span className={classes.dot} data-ok={t.ok || undefined} />
              <Text size="xs" className={classes.status}>
                {t.status}
              </Text>
            </Group>
          </UnstyledButton>
        );
      })}
      {after}
    </Group>
  );
}
